"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Shield, CircleCheck as CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const tasks = ["Menjaga kehormatan organisasi", "Menjaga marwah organisasi", "Menjaga nilai perjuangan organisasi"];
export default function DewanKehormatan() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="dewankehormatan" className="w-full bg-background py-16 md:py-24 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 max-w-4xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground uppercase tracking-wider">
            Dewan Kehormatan
          </h2>
          <div className="w-24 h-1.5 bg-secondary mx-auto mt-6 rounded-md"></div>
        </div>

        <Card className="border-t-4 border-t-secondary shadow-lg bg-card">
          <CardHeader className="pb-6 pt-8">
            <CardTitle className="text-2xl md:text-3xl font-default text-center flex flex-col sm:flex-row items-center justify-center gap-4 text-foreground">
              <div className="p-3 bg-accent rounded-lg">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              Tugas Pokok
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-8 md:px-10">
            <ul className="space-y-4">
              {tasks.map((task, i) => <li key={i} data-index={i} className="flex items-start gap-4 p-5 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 hover:border-border transition-colors duration-300">
                  <CheckCircle className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                  <span className="text-lg md:text-xl font-medium text-foreground leading-relaxed">
                    {task}
                  </span>
                </li>)}
            </ul>
          </CardContent>
        </Card>
      </div>
    </section>;
}